import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { FormErrors } from 'redux-form';
import { css } from 'aphrodite/no-important';
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import FormattedCurrency from '@contactcentre-web/common/FormattedCurrency';
import MoneyField from '@contactcentre-web/common/MoneyField';
import StackedLabel from '@contactcentre-web/common/StackedLabel';
import Button from '@contactcentre-web/common/Button';
import TextField from '@contactcentre-web/common/TextField';
import Checkbox from '@contactcentre-web/common/Checkbox';
import { canApproveCompensations } from '@contactcentre-web/authentication/redux/selectors';
import { getProducts } from '@contactcentre-web/redux-common/selectors/products';
import RefundReason from '@contactcentre-web/refunds/common/RefundReason';
import type State from '@contactcentre-web/redux-common/types/State';
import type Price from '@contactcentre-web/redux-common/types/Price';
import { CompensationHistory } from '@contactcentre-web/hooks/api/useOrderHistory';
import type { Product } from '@contactcentre-web/redux-common/types/Product';

import CompensationRequestConfirmation from '../CompensationRequestConfirmation/CompensationRequestConfirmation';
import selectors from '../../selectors';
import { FORM_ID } from '../../module';
import type { PendingCompensation, RehydratedReasonCode } from '../../reducer';
import type { RequestCompensationPayload, EditCompensationRequestPayload } from '../../sagas';

import styles from './stylesProductsForm';
import messages from './messages';
import { mapProductToCheckbox, mapProductVendor, mapRequestProductValuesPayload } from './utils';

interface CompensationData {
  products: Array<string>;
  amount: string;
  note: string;
  reasonCode: string;
  productValues: Record<
    string,
    {
      checked: boolean;
      value: string;
    }
  >;
}

export interface Props {
  amount: string;
  orderAmount: Price | Record<string, never>;
  compensationReasons: Array<RehydratedReasonCode>;
  paymentType?: string;
  disabled: boolean;
  canConfirmCompensation: boolean;
  compensation?: PendingCompensation;
  previousCompensation?: CompensationHistory;
  products: Array<Product>;
  hasRefunds: boolean;
  requestCompensation: (data: RequestCompensationPayload | EditCompensationRequestPayload) => void;
  productValues: Record<
    string,
    {
      checked: boolean;
      value: string;
    }
  >;
}

export interface Warning {
  type:
    | 'amountWarning'
    | 'previousRefundWarning'
    | 'approveWarning'
    | 'previousCompensationWarning';
  amount: number;
  currencyCode: string;
  requestedDate?: Date;
  orderAmount?: number;
}

const getWarning = (
  canConfirmCompensation: boolean,
  hasRefunds: boolean,
  compensationAmount: number,
  orderAmount: number,
  currencyCode: string,
  previousCompensation?: CompensationHistory
): Warning | null => {
  if (previousCompensation) {
    return {
      ...previousCompensation.payload.requestedAmount,
      requestedDate: previousCompensation.date,
      type: 'previousCompensationWarning',
    };
  }

  if (hasRefunds) {
    return {
      amount: compensationAmount,
      currencyCode,
      type: 'previousRefundWarning',
    };
  }

  if (compensationAmount > orderAmount) {
    return {
      amount: compensationAmount,
      orderAmount,
      currencyCode,
      type: 'amountWarning',
    };
  }

  if (canConfirmCompensation) {
    return {
      amount: compensationAmount,
      currencyCode,
      type: 'approveWarning',
    };
  }

  return null;
};

export const CompensationForm: FunctionComponent<
  Props & InjectedFormProps<CompensationData, Props>
> = ({
  amount,
  orderAmount: { amount: orderAmount, currencyCode },
  compensationReasons,
  paymentType,
  initialize,
  handleSubmit,
  submitting,
  invalid,
  disabled,
  canConfirmCompensation,
  products,
  compensation,
  previousCompensation,
  hasRefunds,
  requestCompensation,
  productValues,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (compensation) {
      initialize({
        amount: String(compensation.value.amount),
        reasonCode: compensation.reasonCodeId,
        note: compensation.note,
        products: [],
      });
    } else {
      initialize({
        productValues: products.reduce(
          (acc, product) => ({ ...acc, [product.id]: { checked: true } }),
          {}
        ),
      });
    }
  }, [compensation]);

  const amountNum = compensation
    ? parseFloat(amount || '0')
    : Object.values(productValues || {}).reduce(
        (acc, product) => acc + parseFloat(product?.value ?? '0'),
        0
      );
  const hasProducts =
    productValues && Object.values(productValues).some(({ checked }) => Boolean(checked));
  const warning = getWarning(
    canConfirmCompensation,
    hasRefunds,
    amountNum,
    orderAmount,
    currencyCode,
    previousCompensation
  );

  const onRequestCompensation = ({ amount, reasonCode, note }: CompensationData) =>
    requestCompensation({
      reasonCodeId: reasonCode,
      note: note.trim(),
      products: !compensation
        ? Object.entries(productValues)
            .filter((selectedProduct) => selectedProduct[1].checked)
            .map(([key]) => ({
              productId: key,
              vendor: mapProductVendor(products)?.[key],
            }))
        : undefined,
      value: { amount: compensation ? parseFloat(amount) : amountNum, currencyCode },
      productValues: !compensation
        ? mapRequestProductValuesPayload(productValues, products, currencyCode)
        : undefined,
    });

  return (
    <form
      id={FORM_ID}
      onSubmit={handleSubmit((values) => {
        if (!invalid && warning) {
          setVisible(true);
        } else {
          onRequestCompensation(values);
        }
      })}
      data-testid="compensationForm"
    >
      {visible && (
        <CompensationRequestConfirmation
          isProcessing={false}
          onProcess={handleSubmit((values) => {
            setVisible(false);
            onRequestCompensation(values);
          })}
          onClose={() => setVisible(false)}
          warning={warning}
        />
      )}
      <section className={css(styles.formContainer)}>
        {!compensation && (
          <div className={css(styles.productsContainer)}>
            <div className={css(styles.productsHeader)}>
              <span>
                <FormattedMessage {...messages.productsLabel} />
              </span>
              <span>
                <FormattedMessage {...messages.amount} />
              </span>
            </div>
            {products.map((product) => (
              <div key={product.id} className={css(styles.row, !hasProducts && styles.rowError)}>
                <Checkbox
                  name={`productValues.${product.id}.checked`}
                  label={mapProductToCheckbox(product).label}
                />
                <MoneyField
                  name={`productValues.${product.id}.value`}
                  currencyText={currencyCode}
                  messages={messages}
                  disabled={!productValues?.[product.id].checked}
                />
              </div>
            ))}
            {!hasProducts && (
              <div className={css(styles.productsError)}>
                <FormattedMessage {...messages.products_required} />
              </div>
            )}
          </div>
        )}
        {/* When editing compensations only show total value */}
        {compensation && (
          <div className={css(styles.row)}>
            <span>
              <FormattedMessage {...messages.amount} />
            </span>
            <MoneyField
              name="amount"
              currencyText={currencyCode}
              messages={messages}
              disabled={disabled}
            />
          </div>
        )}
        <div className={css(styles.detailsContainer)}>
          <RefundReason
            reasons={compensationReasons}
            disabled={disabled}
            styleSheet={{ container: styles.detailsItem }}
          />

          <div className={css(styles.detailsItem)}>
            <FormattedMessage {...messages.noteLabel} />
            <TextField
              name="note"
              type="multiline-text"
              maxLength={1000}
              rows={4}
              messages={messages}
              disabled={disabled}
            />
          </div>
        </div>
      </section>
      <div className={css(styles.footerContainer)}>
        <div>
          <StackedLabel
            label={<FormattedMessage {...messages.totalToRefund} />}
            styleSheet={{
              label: {
                ...styles.label,
              },
              value: styles.amount,
              container: styles.paymentLabelContainer,
            }}
          >
            <FormattedCurrency amount={amountNum < 0 ? 0 : amountNum} currencyCode={currencyCode} />
          </StackedLabel>
          <StackedLabel
            label={<FormattedMessage {...messages.paymentDetails} />}
            styleSheet={{
              label: {
                ...styles.label,
                ...styles.gray,
              },
              container: styles.paymentLabelContainer,
            }}
          >
            {paymentType}
          </StackedLabel>
        </div>
        <Button
          variant="primary"
          size="large"
          testId="compensationSubmit"
          type="submit"
          loading={submitting}
          disabled={!hasProducts || submitting || disabled}
          styleSheet={styles.proceedButton}
        >
          <FormattedMessage
            {...messages[canConfirmCompensation ? 'processButton' : 'submitRequestButton']}
          />
        </Button>
      </div>
    </form>
  );
};

const mapStateToProps = (state: State) => ({
  amount: formValueSelector(FORM_ID)(state, 'amount') as CompensationData['amount'],
  productValues: formValueSelector(FORM_ID)(
    state,
    'productValues'
  ) as CompensationData['productValues'],
  reasonCode: formValueSelector(FORM_ID)(state, 'reasonCode') as string,
  canConfirmCompensation: canApproveCompensations(state),
  products: getProducts(state),
  hasRefunds: selectors.hasRefunds(state),
});

export const validate = (
  { amount, reasonCode, note, productValues }: CompensationData,
  { compensation }: Props
): FormErrors<CompensationData, string> => {
  const productValuesErrors: { [key: string]: { value?: string; checked?: string } } = {}; // Errors for individual product values

  if (productValues) {
    Object.entries(productValues).forEach(([productId, { checked, value }]) => {
      if (checked && (!value || isNaN(Number(value)) || parseFloat(value) <= 0)) {
        productValuesErrors[productId] = { value: 'invalidAmount' };
      }
    });
  }

  return {
    amount: compensation && (!amount || parseFloat(amount) <= 0) ? 'invalidAmount' : undefined,
    reasonCode: !reasonCode ? 'required' : undefined,
    note:
      !note || note.trim() === ''
        ? 'note_required'
        : note.trim().length > 1000
        ? 'noteLengthExceeded'
        : undefined,
    productValues:
      Object.keys(productValuesErrors).length > 0 ? (productValuesErrors as any) : undefined,
  };
};

export default connect(mapStateToProps)(
  reduxForm<CompensationData, Props>({
    form: FORM_ID,
    validate,
  })(CompensationForm)
);
